import React, { useEffect, useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import { Box, Drawer, DrawerContent, DrawerOverlay, IconButton, Text, useDisclosure } from '@chakra-ui/react';
import {
    DashboardOutlined, Dashboard as DashboardFilled, ReceiptLongOutlined, ReceiptLong, Inventory2Outlined, Inventory2,
    CategoryOutlined, Category, ViewCarouselOutlined, ViewCarousel, ReportProblemOutlined, ReportProblem,
    ForumOutlined, Forum, ExtensionOutlined, Extension, MoreHoriz, Menu as MenuIcon, Close, OpenInNew, Logout
} from '@mui/icons-material';

import Logo from '../layout/Logo';
import { useUserContext } from '../../contexts/UserContext';
import { getUserById } from '../../services/UserServices';

export const ADMIN_NAV = [
    { label: 'Dashboard', path: '/admin', icon: DashboardOutlined, active: DashboardFilled },
    { label: 'Orders', path: '/admin/orders', icon: ReceiptLongOutlined, active: ReceiptLong },
    { label: 'Products', path: '/admin/products', icon: Inventory2Outlined, active: Inventory2 },
    { label: 'Categories', path: '/admin/categories', icon: CategoryOutlined, active: Category },
    { label: 'Banners', path: '/admin/images', icon: ViewCarouselOutlined, active: ViewCarousel },
    { label: 'Reports', path: '/admin/reports', icon: ReportProblemOutlined, active: ReportProblem },
    { label: 'Communications', path: '/admin/communications', icon: ForumOutlined, active: Forum },
    { label: 'Integrations', path: '/admin/integrations', icon: ExtensionOutlined, active: Extension }
];

const PRIMARY = ADMIN_NAV.slice(0, 3);

const AdminChip = () => (
    <Box as='span' px='7px' py='2px' borderRadius='4px' bg='raano.black' color='raano.white' fontSize='10px' fontWeight={700} letterSpacing='0.1em' >ADMIN</Box>
);

// Sidebar contents, shared by the desktop rail and the phone/tablet drawer
const SidebarBody = ({ isActive, go, user, onLogout }) => (
    <Box display='flex' flexDirection='column' height='100%' >
        <Box display='flex' alignItems='center' gap='10px' px='20px' height='72px' flexShrink={0} >
            <Logo size='24px' to='/admin' />
            <AdminChip />
        </Box>
        <Box as='nav' aria-label='Admin' flex={1} overflowY='auto' px='12px' pt='4px' >
            {ADMIN_NAV.map(({ label, path, icon: Icon, active: ActiveIcon }) => {
                const on = isActive(path);
                return (
                    <Box key={path} as='button' width='100%' height='40px' display='flex' alignItems='center' gap='12px' px='12px' mb='2px' borderRadius='8px'
                        fontSize='14px' fontWeight={500} textAlign='left' aria-current={on ? 'page' : undefined}
                        bg={on ? 'raano.black' : 'transparent'} color={on ? 'raano.white' : 'raano.black'}
                        transition='background .125s' _hover={on ? undefined : { bg: 'raano.surface' }} onClick={() => go(path)} >
                        {on ? <ActiveIcon sx={{ fontSize: 20 }} /> : <Icon sx={{ fontSize: 20 }} />}{label}
                    </Box>
                );
            })}
        </Box>
        <Box px='12px' pb='16px' pt='8px' borderTop='1px solid' borderColor='raano.border' flexShrink={0} >
            <Box as={RouterLink} to='/' display='flex' alignItems='center' gap='12px' height='40px' px='12px' borderRadius='8px' fontSize='14px' _hover={{ bg: 'raano.surface' }} >
                <OpenInNew sx={{ fontSize: 20 }} />View store
            </Box>
            <Box display='flex' alignItems='center' gap='10px' px='12px' mt='8px' >
                <Box width='32px' height='32px' borderRadius='full' bg='raano.black' color='raano.white' display='flex' alignItems='center' justifyContent='center' fontSize='13px' fontWeight={700} flexShrink={0} >
                    {(user?.firstName || 'A')[0].toUpperCase()}
                </Box>
                <Box flex={1} minWidth={0} >
                    <Text fontSize='13px' fontWeight={600} noOfLines={1} >{user ? `${user.firstName} ${user.lastName || ''}` : 'Admin'}</Text>
                    <Text fontSize='12px' color='raano.muted' noOfLines={1} >{user?.email || ''}</Text>
                </Box>
                <IconButton size='sm' variant='ghost' aria-label='Log out' icon={<Logout sx={{ fontSize: 18 }} />} onClick={onLogout} />
            </Box>
        </Box>
    </Box>
);

// Shell for every admin page (design-system.md §12.1): sidebar on desktop; top bar, drawer and bottom tabs below 1025px
const AdminLayout = ({ title, subtitle, actions, children }) => {

    const navigate = useNavigate();
    const { pathname } = useLocation();
    const { currentUser, logout } = useUserContext();
    const drawer = useDisclosure();
    const more = useDisclosure();
    const [user, setUser] = useState(null);

    useEffect(() => {
        if (currentUser) getUserById(currentUser).then(r => setUser(r.user)).catch(() => { });
    }, [currentUser]);

    const isActive = (path) => path === '/admin' ? pathname === '/admin' : pathname.startsWith(path);
    const go = (path) => { drawer.onClose(); more.onClose(); navigate(path); };
    const onLogout = () => { logout(); navigate('/'); };
    const moreActive = ADMIN_NAV.slice(3).some(item => isActive(item.path));

    return (
        <Box bg='raano.surface' minHeight='100vh' >
            {/* Desktop sidebar */}
            <Box display={{ base: 'none', lg: 'block' }} position='fixed' top={0} left={0} bottom={0} width='248px' bg='raano.white' borderRight='1px solid' borderColor='raano.border' zIndex={300} >
                <SidebarBody isActive={isActive} go={go} user={user} onLogout={onLogout} />
            </Box>

            {/* Phone / tablet top bar */}
            <Box display={{ base: 'flex', lg: 'none' }} position='sticky' top={0} zIndex={300} height='56px' alignItems='center' gap='6px' px='6px' bg='raano.white' borderBottom='1px solid' borderColor='raano.border' >
                <IconButton variant='ghost' aria-label='Open admin menu' icon={<MenuIcon />} onClick={drawer.onOpen} />
                <Logo size='22px' to='/admin' />
                <AdminChip />
                <Box ml='auto' as={RouterLink} to='/' display='flex' alignItems='center' gap='4px' fontSize='13px' px='10px' >Store<OpenInNew sx={{ fontSize: 16 }} /></Box>
            </Box>

            <Drawer isOpen={drawer.isOpen} onClose={drawer.onClose} placement='left' >
                <DrawerOverlay />
                <DrawerContent maxWidth='280px' >
                    <IconButton position='absolute' top='16px' right='12px' size='sm' variant='ghost' aria-label='Close menu' icon={<Close />} onClick={drawer.onClose} zIndex={1} />
                    <SidebarBody isActive={isActive} go={go} user={user} onLogout={onLogout} />
                </DrawerContent>
            </Drawer>

            {/* Content */}
            <Box ml={{ base: 0, lg: '248px' }} >
                <Box maxWidth='1200px' mx='auto' px={{ base: '12px', md: '24px', lg: '40px' }} pt={{ base: '16px', md: '24px', lg: '32px' }} pb={{ base: '96px', lg: '48px' }} >
                    <Box display='flex' flexWrap='wrap' justifyContent='space-between' alignItems='flex-start' gap='12px' mb={{ base: '16px', md: '24px' }} >
                        <Box minWidth={0} >
                            <Text as='h1' fontSize={{ base: '26px', md: '32px' }} fontWeight={700} textTransform='uppercase' lineHeight={1} sx={{ fontStretch: '75%' }} >{title}</Text>
                            {subtitle && <Text fontSize='14px' color='raano.muted' mt='6px' >{subtitle}</Text>}
                        </Box>
                        {actions && <Box flexShrink={0} display='flex' gap='8px' flexWrap='wrap' >{actions}</Box>}
                    </Box>
                    {children}
                </Box>
            </Box>

            {/* Phone / tablet tab bar */}
            <Box as='nav' aria-label='Admin tabs' display={{ base: 'flex', lg: 'none' }} position='fixed' bottom={0} left={0} right={0} height='64px' zIndex={400} bg='raano.white' borderTopRadius='tabbar' boxShadow='tabbar' alignItems='center' justifyContent='space-around' pb='env(safe-area-inset-bottom)' >
                {PRIMARY.map(({ label, path, icon: Icon, active: ActiveIcon }) => (
                    <Tab key={path} label={label} on={isActive(path)} Icon={isActive(path) ? ActiveIcon : Icon} onClick={() => go(path)} />
                ))}
                <Tab label='More' on={moreActive || more.isOpen} Icon={MoreHoriz} onClick={more.onOpen} />
            </Box>

            <Drawer isOpen={more.isOpen} onClose={more.onClose} placement='bottom' >
                <DrawerOverlay />
                <DrawerContent borderTopRadius='20px' pb='calc(16px + env(safe-area-inset-bottom))' >
                    <Box mx='auto' mt='8px' mb='4px' width='40px' height='4px' borderRadius='full' bg='raano.border' />
                    <Box px='12px' >
                        {ADMIN_NAV.slice(3).map(({ label, path, icon: Icon, active: ActiveIcon }) => {
                            const on = isActive(path);
                            return (
                                <Box key={path} as='button' width='100%' height='52px' display='flex' alignItems='center' gap='14px' px='12px' borderRadius='8px' fontSize='15px'
                                    bg={on ? 'raano.black' : 'transparent'} color={on ? 'raano.white' : 'raano.black'} onClick={() => go(path)} >
                                    {on ? <ActiveIcon /> : <Icon />}{label}
                                </Box>
                            );
                        })}
                    </Box>
                </DrawerContent>
            </Drawer>
        </Box>
    );
};

const Tab = ({ label, on, Icon, onClick }) => (
    <Box as='button' aria-current={on ? 'page' : undefined} width='72px' display='flex' flexDirection='column' alignItems='center' gap='2px' color={on ? 'raano.black' : 'blackAlpha.600'} onClick={onClick} >
        <Icon sx={{ fontSize: 24 }} />
        <Text fontSize='11px' fontWeight={on ? 700 : 500} >{label}</Text>
    </Box>
);

export default AdminLayout;
